import { Booking, Resource, WorkingHoursProfile, Absence, BankHoliday } from '@/types';
import { startOfDay, endOfDay, getDay, isSameDay, parseISO } from 'date-fns';

/**
 * Get the day key for WorkingHoursProfile based on a date
 */
function getDayKey(date: Date): keyof WorkingHoursProfile {
  const dayOfWeek = getDay(date); // 0 = Sunday, 1 = Monday, etc.
  const dayKeys: (keyof WorkingHoursProfile)[] = [
    'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'
  ];
  return dayKeys[dayOfWeek];
}

/**
 * Get working hours for a resource on a specific day
 * Uses custom profile if enabled, otherwise falls back to global profile
 */
export function getWorkingHoursForDay(
  resource: Resource,
  date: Date,
  globalWorkingProfile: WorkingHoursProfile
): number {
  const dayKey = getDayKey(date);
  
  // If resource has custom profile enabled and has a custom profile, use it
  if (resource.useCustomWorkingProfile && resource.customWorkingProfile) {
    return resource.customWorkingProfile[dayKey] || 0;
  }
  
  // Otherwise use global working profile
  return globalWorkingProfile[dayKey] || 0;
}

/**
 * Calculate absence minutes for a resource on a specific day
 */
function calculateAbsenceMinutes(
  resourceId: string,
  date: Date,
  absences: Absence[],
  workingHoursPerDay: number
): number {
  const dayStart = startOfDay(date);
  const dayEnd = endOfDay(date);
  let absenceMinutes = 0;

  // Filter absences for this resource on this day
  const dayAbsences = absences.filter((absence) => {
    const absenceStart = parseISO(absence.startDate);
    const absenceEnd = parseISO(absence.endDate);
    
    return (
      String(absence.resourceId) === String(resourceId) &&
      absenceStart <= dayEnd &&
      absenceEnd >= dayStart
    );
  });

  dayAbsences.forEach((absence) => {
    switch (absence.type) {
      case 'full-day':
        // Full-day absence uses all working hours
        absenceMinutes += workingHoursPerDay * 60;
        break;
      case 'half-day-am':
      case 'half-day-pm':
        // Half-day absence uses half of working hours
        absenceMinutes += (workingHoursPerDay * 60) / 2;
        break;
      case 'hourly':
        // Hourly absence - calculate actual duration
        if (absence.startTime && absence.endTime) {
          const [startHour, startMin] = absence.startTime.split(':').map(Number);
          const [endHour, endMin] = absence.endTime.split(':').map(Number);
          const startMinutes = startHour * 60 + startMin;
          const endMinutes = endHour * 60 + endMin;
          absenceMinutes += endMinutes - startMinutes;
        }
        break;
    }
  });

  return absenceMinutes;
}

/**
 * Check if a date is a bank holiday
 */
function isBankHoliday(date: Date, bankHolidays: BankHoliday[]): boolean {
  return bankHolidays.some((holiday) => {
    const holidayDate = parseISO(holiday.date);
    return isSameDay(date, holidayDate);
  });
}

/**
 * Calculate resource utilization percentage for a specific day
 * Now supports working profiles (global and custom), excludes cancelled bookings,
 * and includes absences and bank holidays in the calculation
 */
export function calculateDailyUtilization(
  resourceId: string,
  date: Date,
  bookings: Booking[],
  resource?: Resource,
  globalWorkingProfile?: WorkingHoursProfile,
  absences?: Absence[],
  bankHolidays?: BankHoliday[]
): number {
  const dayStart = startOfDay(date);
  const dayEnd = endOfDay(date);

  // Get working hours for this day based on profile
  let workingHoursPerDay = 8; // Default fallback
  
  if (resource && globalWorkingProfile) {
    workingHoursPerDay = getWorkingHoursForDay(resource, date, globalWorkingProfile);
  }
  
  // If no working hours for this day (e.g., weekend), return 0 utilization
  if (workingHoursPerDay === 0) {
    return 0;
  }

  // Check if this day is a bank holiday - if so, return 0 (no capacity available)
  if (bankHolidays && isBankHoliday(date, bankHolidays)) {
    return 0;
  }

  // Filter bookings for this resource on this day
  // Exclude cancelled and planned bookings (neither represents committed work)
  const dayBookings = bookings.filter((booking) => {
    const bookingStart = new Date(booking.startTime);
    const bookingEnd = new Date(booking.endTime);
    
    if (booking.status === 'cancelled' || booking.status === 'planned') {
      return false;
    }
    
    return (
      String(booking.resourceId) === String(resourceId) &&
      bookingStart < dayEnd &&
      bookingEnd > dayStart
    );
  });

  // Calculate total booked minutes for this day
  let totalBookedMinutes = 0;
  
  dayBookings.forEach((booking) => {
    const bookingStart = new Date(booking.startTime);
    const bookingEnd = new Date(booking.endTime);
    
    // For full-day bookings, use the conditioned hours for that day
    if (booking.durationType === 'full-day') {
      // Full-day booking uses the working hours for that day
      totalBookedMinutes += workingHoursPerDay * 60;
    } else if (booking.durationType === 'half-day' || booking.durationType === 'half-day-am' || booking.durationType === 'half-day-pm') {
      // Half-day booking uses half of the working hours
      totalBookedMinutes += (workingHoursPerDay * 60) / 2;
    } else {
      // Hourly booking - calculate actual duration
      // Clamp booking times to the day boundaries
      const effectiveStart = bookingStart < dayStart ? dayStart : bookingStart;
      const effectiveEnd = bookingEnd > dayEnd ? dayEnd : bookingEnd;
      
      const minutes = (effectiveEnd.getTime() - effectiveStart.getTime()) / (1000 * 60);
      totalBookedMinutes += minutes;
    }
  });

  // Add absence minutes for this day using the helper function
  // that correctly handles all absence types (full-day, half-day-am, half-day-pm, hourly)
  if (absences) {
    const absenceMinutes = calculateAbsenceMinutes(resourceId, date, absences, workingHoursPerDay);
    totalBookedMinutes += absenceMinutes;
  }

  // If no bookings or absences, return 0
  if (totalBookedMinutes === 0) return 0;

  // Calculate utilization based on working hours from profile
  const workingMinutesPerDay = workingHoursPerDay * 60;
  const utilization = (totalBookedMinutes / workingMinutesPerDay) * 100;
  
  return Math.round(Math.min(utilization, 999)); // Cap at 999% for display
}


/**
 * Calculate utilization for all resources on a specific date
 * Now supports working profiles (global and custom)
 */
export function calculateDateUtilization(
  date: Date,
  resources: Resource[],
  bookings: Booking[],
  globalWorkingProfile?: WorkingHoursProfile
): Map<string, number> {
  const utilizationMap = new Map<string, number>();
  
  resources.forEach((resource) => {
    const utilization = calculateDailyUtilization(
      resource.id, 
      date, 
      bookings, 
      resource, 
      globalWorkingProfile
    );
    utilizationMap.set(resource.id, utilization);
  });
  
  return utilizationMap;
}

/**
 * Calculate average utilization across all resources for a date
 * Now supports working profiles (global and custom)
 */
export function calculateAverageUtilization(
  date: Date,
  resources: Resource[],
  bookings: Booking[],
  globalWorkingProfile?: WorkingHoursProfile
): number {
  if (resources.length === 0) return 0;
  
  const utilizationMap = calculateDateUtilization(date, resources, bookings, globalWorkingProfile);
  const total = Array.from(utilizationMap.values()).reduce((sum, util) => sum + util, 0);
  
  return Math.round(total / resources.length);
}

/**
 * Calculate resource utilization percentage for a specific week
 * Averages the daily utilization across all days in the week
 */
export function calculateWeeklyUtilization(
  resourceId: string,
  weekStart: Date,
  weekEnd: Date,
  bookings: Booking[],
  resource?: Resource,
  globalWorkingProfile?: WorkingHoursProfile
): number {
  const days: Date[] = [];
  const currentDate = new Date(weekStart);
  
  // Generate all days in the week
  while (currentDate <= weekEnd) {
    days.push(new Date(currentDate));
    currentDate.setDate(currentDate.getDate() + 1);
  }
  
  // Calculate average utilization across all days
  const dailyUtilizations = days.map(day => 
    calculateDailyUtilization(resourceId, day, bookings, resource, globalWorkingProfile)
  );
  
  const totalUtilization = dailyUtilizations.reduce((sum, util) => sum + util, 0);
  const averageUtilization = totalUtilization / days.length;
  
  return Math.round(averageUtilization);
}

/**
 * Calculate resource utilization percentage for a specific month
 * Averages the daily utilization across all days in the month
 */
export function calculateMonthlyUtilization(
  resourceId: string,
  monthStart: Date,
  monthEnd: Date,
  bookings: Booking[],
  resource?: Resource,
  globalWorkingProfile?: WorkingHoursProfile
): number {
  const days: Date[] = [];
  const currentDate = new Date(monthStart);
  
  // Generate all days in the month
  while (currentDate <= monthEnd) {
    days.push(new Date(currentDate));
    currentDate.setDate(currentDate.getDate() + 1);
  }
  
  // Calculate average utilization across all days
  const dailyUtilizations = days.map(day => 
    calculateDailyUtilization(resourceId, day, bookings, resource, globalWorkingProfile)
  );
  
  const totalUtilization = dailyUtilizations.reduce((sum, util) => sum + util, 0);
  const averageUtilization = totalUtilization / days.length;
  
  return Math.round(averageUtilization);
}


/**
 * Calculate total booked hours for a resource on a specific day
 * Excludes cancelled bookings
 */
export function calculateTotalBookedHours(
  resourceId: string,
  date: Date,
  bookings: Booking[]
): number {
  const dayStart = startOfDay(date);
  const dayEnd = endOfDay(date);

  // Filter bookings for this resource on this day
  // Exclude cancelled and planned bookings (neither represents committed work)
  const dayBookings = bookings.filter((booking) => {
    const bookingStart = new Date(booking.startTime);
    const bookingEnd = new Date(booking.endTime);
    
    if (booking.status === 'cancelled' || booking.status === 'planned') {
      return false;
    }
    
    return (
      String(booking.resourceId) === String(resourceId) &&
      bookingStart < dayEnd &&
      bookingEnd > dayStart
    );
  });

  if (dayBookings.length === 0) return 0;

  // Calculate total booked hours for this day
  let totalBookedMinutes = 0;
  
  dayBookings.forEach((booking) => {
    const bookingStart = new Date(booking.startTime);
    const bookingEnd = new Date(booking.endTime);
    
    // For full-day bookings, use 8 hours as default
    if (booking.durationType === 'full-day') {
      totalBookedMinutes += 8 * 60;
    } else if (booking.durationType === 'half-day' || booking.durationType === 'half-day-am' || booking.durationType === 'half-day-pm') {
      // Half-day booking uses 4 hours
      totalBookedMinutes += 4 * 60;
    } else {
      // Hourly booking - calculate actual duration
      // Clamp booking times to the day boundaries
      const effectiveStart = bookingStart < dayStart ? dayStart : bookingStart;
      const effectiveEnd = bookingEnd > dayEnd ? dayEnd : bookingEnd;
      
      const minutes = (effectiveEnd.getTime() - effectiveStart.getTime()) / (1000 * 60);
      totalBookedMinutes += minutes;
    }
  });

  // Return total hours (rounded to 1 decimal place)
  return Math.round((totalBookedMinutes / 60) * 10) / 10;
}

/**
 * Calculate total booked hours for a resource across a week
 * Excludes cancelled bookings
 */
export function calculateWeeklyBookedHours(
  resourceId: string,
  weekStart: Date,
  weekEnd: Date,
  bookings: Booking[]
): number {
  const days: Date[] = [];
  const currentDate = new Date(weekStart);
  
  // Generate all days in the week
  while (currentDate <= weekEnd) {
    days.push(new Date(currentDate));
    currentDate.setDate(currentDate.getDate() + 1);
  }
  
  // Sum up total booked hours across all days
  const totalHours = days.reduce((sum, day) => {
    return sum + calculateTotalBookedHours(resourceId, day, bookings);
  }, 0);
  
  return Math.round(totalHours * 10) / 10;
}

/**
 * Calculate total booked hours for a resource across a month
 * Excludes cancelled bookings
 */
export function calculateMonthlyBookedHours(
  resourceId: string,
  monthStart: Date,
  monthEnd: Date,
  bookings: Booking[]
): number {
  const days: Date[] = [];
  const currentDate = new Date(monthStart);
  
  // Generate all days in the month
  while (currentDate <= monthEnd) {
    days.push(new Date(currentDate));
    currentDate.setDate(currentDate.getDate() + 1);
  }
  
  // Sum up total booked hours across all days
  const totalHours = days.reduce((sum, day) => {
    return sum + calculateTotalBookedHours(resourceId, day, bookings);
  }, 0);
  
  return Math.round(totalHours * 10) / 10;
}

/**
 * Calculate available hours for a resource on a specific day based on working profile
 */
export function calculateAvailableHours(
  resource: Resource,
  date: Date,
  globalWorkingProfile: WorkingHoursProfile
): number {
  return getWorkingHoursForDay(resource, date, globalWorkingProfile);
}

/**
 * Calculate available hours for a resource across a week based on working profile
 */
export function calculateWeeklyAvailableHours(
  resource: Resource,
  weekStart: Date,
  weekEnd: Date,
  globalWorkingProfile: WorkingHoursProfile
): number {
  const days: Date[] = [];
  const currentDate = new Date(weekStart);
  
  // Generate all days in the week
  while (currentDate <= weekEnd) {
    days.push(new Date(currentDate));
    currentDate.setDate(currentDate.getDate() + 1);
  }
  
  // Sum up available hours across all days
  const totalHours = days.reduce((sum, day) => {
    return sum + getWorkingHoursForDay(resource, day, globalWorkingProfile);
  }, 0);
  
  return Math.round(totalHours * 10) / 10;
}

/**
 * Calculate available hours for a resource across a month based on working profile
 */
export function calculateMonthlyAvailableHours(
  resource: Resource,
  monthStart: Date,
  monthEnd: Date,
  globalWorkingProfile: WorkingHoursProfile
): number {
  const days: Date[] = [];
  const currentDate = new Date(monthStart);
  
  // Generate all days in the month
  while (currentDate <= monthEnd) {
    days.push(new Date(currentDate));
    currentDate.setDate(currentDate.getDate() + 1);
  }
  
  // Sum up available hours across all days
  const totalHours = days.reduce((sum, day) => {
    return sum + getWorkingHoursForDay(resource, day, globalWorkingProfile);
  }, 0);
  
  return Math.round(totalHours * 10) / 10;
}

/**
 * Calculate remaining hours for a resource on a specific day
 * Remaining = Available - Booked
 */
export function calculateRemainingHours(
  resourceId: string,
  date: Date,
  bookings: Booking[],
  resource: Resource,
  globalWorkingProfile: WorkingHoursProfile
): number {
  const availableHours = calculateAvailableHours(resource, date, globalWorkingProfile);
  const bookedHours = calculateTotalBookedHours(resourceId, date, bookings);
  const remaining = availableHours - bookedHours;
  
  return Math.round(remaining * 10) / 10;
}

/**
 * Calculate remaining hours for a resource across a week
 * Remaining = Available - Booked
 */
export function calculateWeeklyRemainingHours(
  resourceId: string,
  weekStart: Date,
  weekEnd: Date,
  bookings: Booking[],
  resource: Resource,
  globalWorkingProfile: WorkingHoursProfile
): number {
  const availableHours = calculateWeeklyAvailableHours(resource, weekStart, weekEnd, globalWorkingProfile);
  const bookedHours = calculateWeeklyBookedHours(resourceId, weekStart, weekEnd, bookings);
  const remaining = availableHours - bookedHours;
  
  return Math.round(remaining * 10) / 10;
}

/**
 * Calculate remaining hours for a resource across a month
 * Remaining = Available - Booked
 */
export function calculateMonthlyRemainingHours(
  resourceId: string,
  monthStart: Date,
  monthEnd: Date,
  bookings: Booking[],
  resource: Resource,
  globalWorkingProfile: WorkingHoursProfile
): number {
  const availableHours = calculateMonthlyAvailableHours(resource, monthStart, monthEnd, globalWorkingProfile);
  const bookedHours = calculateMonthlyBookedHours(resourceId, monthStart, monthEnd, bookings);
  const remaining = availableHours - bookedHours;
  
  return Math.round(remaining * 10) / 10;
}

/**
 * Format hours display with abbreviation
 * Examples: "8h", "24h", "160h", "Bkd: 8h", "Avail: 40h"
 */
export function formatHoursDisplay(hours: number, label?: 'Bkd' | 'Avail' | 'Rem'): string {
  const hoursStr = hours % 1 === 0 ? hours.toString() : hours.toFixed(1);
  
  if (label) {
    return `${label}: ${hoursStr}h`;
  }
  
  return `${hoursStr}h`;
}

/**
 * Get capacity metrics for a resource on a specific period
 * Returns available, booked, and remaining hours
 */
export interface CapacityMetrics {
  available: number;
  booked: number;
  remaining: number;
  utilization: number;
}

export function getCapacityMetrics(
  resourceId: string,
  date: Date,
  bookings: Booking[],
  resource: Resource,
  globalWorkingProfile: WorkingHoursProfile
): CapacityMetrics {
  const available = calculateAvailableHours(resource, date, globalWorkingProfile);
  const booked = calculateTotalBookedHours(resourceId, date, bookings);
  const remaining = available - booked;
  const utilization = available > 0 ? Math.round((booked / available) * 100) : 0;
  
  return {
    available: Math.round(available * 10) / 10,
    booked: Math.round(booked * 10) / 10,
    remaining: Math.round(remaining * 10) / 10,
    utilization
  };
}

export function getWeeklyCapacityMetrics(
  resourceId: string,
  weekStart: Date,
  weekEnd: Date,
  bookings: Booking[],
  resource: Resource,
  globalWorkingProfile: WorkingHoursProfile
): CapacityMetrics {
  const available = calculateWeeklyAvailableHours(resource, weekStart, weekEnd, globalWorkingProfile);
  const booked = calculateWeeklyBookedHours(resourceId, weekStart, weekEnd, bookings);
  const remaining = available - booked;
  const utilization = available > 0 ? Math.round((booked / available) * 100) : 0;
  
  return {
    available: Math.round(available * 10) / 10,
    booked: Math.round(booked * 10) / 10,
    remaining: Math.round(remaining * 10) / 10,
    utilization
  };
}

export function getMonthlyCapacityMetrics(
  resourceId: string,
  monthStart: Date,
  monthEnd: Date,
  bookings: Booking[],
  resource: Resource,
  globalWorkingProfile: WorkingHoursProfile
): CapacityMetrics {
  const available = calculateMonthlyAvailableHours(resource, monthStart, monthEnd, globalWorkingProfile);
  const booked = calculateMonthlyBookedHours(resourceId, monthStart, monthEnd, bookings);
  const remaining = available - booked;
  const utilization = available > 0 ? Math.round((booked / available) * 100) : 0;
  
  return {
    available: Math.round(available * 10) / 10,
    booked: Math.round(booked * 10) / 10,
    remaining: Math.round(remaining * 10) / 10,
    utilization
  };
}

export interface PeriodUtilizationResult {
  totalBookedHours: number;
  totalCapacityHours: number;
  utilizationPercentage: number;
}

export function calculatePeriodUtilization(
  resourceId: string,
  periodStart: Date,
  periodEnd: Date,
  bookings: Booking[],
  resource: Resource,
  globalWorkingProfile: WorkingHoursProfile
): PeriodUtilizationResult {
  const days: Date[] = [];
  const currentDate = new Date(startOfDay(periodStart));
  const end = startOfDay(periodEnd);

  while (currentDate <= end) {
    days.push(new Date(currentDate));
    currentDate.setDate(currentDate.getDate() + 1);
  }

  let totalBookedMinutes = 0;
  let totalCapacityMinutes = 0;

  days.forEach(day => {
    const dayKey = getDayKey(day);

    const workingHoursForDay = resource.useCustomWorkingProfile && resource.customWorkingProfile
      ? resource.customWorkingProfile[dayKey] || 0
      : globalWorkingProfile[dayKey] || 0;

    if (workingHoursForDay === 0) return;

    totalCapacityMinutes += workingHoursForDay * 60;

    const dayStartBound = startOfDay(day);
    const dayEndBound = endOfDay(day);

    const dayBookings = bookings.filter(booking => {
      const bookingStart = new Date(booking.startTime);
      const bookingEnd = new Date(booking.endTime);

      if (booking.status === 'cancelled' || booking.status === 'planned') {
        return false;
      }

      return (
        String(booking.resourceId) === String(resourceId) &&
        bookingStart < dayEndBound &&
        bookingEnd > dayStartBound
      );
    });

    dayBookings.forEach(booking => {
      if (booking.durationType === 'full-day') {
        totalBookedMinutes += workingHoursForDay * 60;
      } else if (booking.durationType === 'half-day' || booking.durationType === 'half-day-am' || booking.durationType === 'half-day-pm') {
        totalBookedMinutes += (workingHoursForDay * 60) / 2;
      } else {
        const bookingStart = new Date(booking.startTime);
        const bookingEnd = new Date(booking.endTime);
        const effectiveStart = bookingStart < dayStartBound ? dayStartBound : bookingStart;
        const effectiveEnd = bookingEnd > dayEndBound ? dayEndBound : bookingEnd;
        const minutes = (effectiveEnd.getTime() - effectiveStart.getTime()) / (1000 * 60);
        totalBookedMinutes += minutes;
      }
    });
  });

  const totalBookedHours = Math.round((totalBookedMinutes / 60) * 10) / 10;
  const totalCapacityHours = Math.round((totalCapacityMinutes / 60) * 10) / 10;
  const utilizationPercentage = totalCapacityHours > 0
    ? Math.round((totalBookedHours / totalCapacityHours) * 100)
    : 0;

  return { totalBookedHours, totalCapacityHours, utilizationPercentage };
}
